import React, {useCallback, useEffect, useMemo, useState} from "react";
import Dialog from "../../base/dialog";
import {Box, DialogActions, DialogContent, DialogTitle} from "@mui/material";
import Button from "../../base/button/button";
import Form, {MuiRFF, Yup} from "../../form";
import Grid2 from "@mui/material/Unstable_Grid2";
import Api from "../../../../core/services/api/api";
import useIsMounted from "../../../hooks/use-is-mounted";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import useAccessLevels from "../../../hooks/use-access-levels";
import ToastService from "../../../../core/services/toast";
import {useTranslation} from "react-i18next";
import usePartnerConfiguration from "../../../hooks/use-partner-configuration";

/**
 * Creates a vendor via CleanAF/Vendor/Create (code?, name, email, phone).
 * Code visibility/requirement follows the same autoGenerateCustomerNumber setting as create-customer.
 */
const CreateVendorDialog = ({open, close, onSuccess}) => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.vendors-list.createDialog'});
    const operatorAccessLevels = useAccessLevels();
    const [submitting, setSubmitting] = useState(false);
    const {getValue} = usePartnerConfiguration();
    const [autoGenerateCode, setAutoGenerateCode] = useState(
        getValue((ids) => ids.autoGenerateCustomerNumber) ?? true,
    );
    const isMounted = useIsMounted();

    const schema = useMemo(() => Yup.object().shape({
        name: Yup.string().trim().nullable().required(ValidateMessages.required),
        code: autoGenerateCode
            ? Yup.string().trim().nullable()
            : Yup.string().trim().nullable().required(ValidateMessages.required),
        email: Yup.string().email(ValidateMessages.incorrect).trim().nullable().required(ValidateMessages.required),
        phone: Yup.string().trim().nullable().required(ValidateMessages.required),
    }), [autoGenerateCode]);

    const validate = useMemo(() => MuiRFF.makeValidate(schema), [schema]);
    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    /**
     * On open: refresh auto-generate flag from partner configuration and enforce createVendor access.
     */
    useEffect(() => {
        if (!open)
            return;
        setAutoGenerateCode(getValue((ids) => ids.autoGenerateCustomerNumber) ?? true);
        if (!operatorAccessLevels.packages.base.createNewVendor) {
            close();
            ToastService.error({
                title: t('accessDeniedTitle'),
                message: t('accessDeniedMessage'),
            });
        }
    }, [close, getValue, open, operatorAccessLevels.packages.base.createNewVendor, t]);

    /**
     * Submits Create; omits code when the system auto-generates codes.
     */
    const createVendor = useCallback(async (values) => {
        setSubmitting(true);
        const phone = values.phone ?? '';
        const forApi = {
            name: values.name,
            email: values.email ?? '',
            phone,
            ...(!autoGenerateCode ? {code: values.code?.trim() ?? ''} : {}),
        };
        const response = await Api.createVendorV2(forApi);
        if (!isMounted())
            return;
        setSubmitting(false);
        if (response?.resultFlag) {
            onSuccess?.(response.data?.id ?? response.data);
            close();
        }
    }, [autoGenerateCode, close, isMounted, onSuccess]);

    return (
        <Dialog open={open} setOpen={close} maxWidth={'sm'}>
            <DialogTitle>{t('title')}</DialogTitle>
            <DialogContent dividers>
                <Form
                    onSubmit={createVendor}
                    validate={validate}
                    formProps={{id: 'create-vendor-form'}}
                >
                    {() => (
                        <Grid2 container spacing={2}>
                            {
                                !autoGenerateCode &&
                                <Grid2 xs={12}>
                                    <MuiRFF.TextField
                                        name={'code'}
                                        label={t('code')}
                                        required={required.code}
                                        fullWidth
                                        disabled={submitting}
                                    />
                                </Grid2>
                            }
                            <Grid2 xs={12}>
                                <MuiRFF.TextField
                                    name={'name'}
                                    label={t('name')}
                                    required={required.name}
                                    fullWidth
                                    disabled={submitting}
                                />
                            </Grid2>
                            <Grid2 xs={12} md={6}>
                                <MuiRFF.TextField
                                    name={'email'}
                                    label={t('email')}
                                    required={required.email}
                                    fullWidth
                                    disabled={submitting}
                                />
                            </Grid2>
                            <Grid2 xs={12} md={6}>
                                <MuiRFF.TextField
                                    name={'phone'}
                                    label={t('phone')}
                                    required={required.phone}
                                    fullWidth
                                    disabled={submitting}
                                />
                            </Grid2>
                        </Grid2>
                    )}
                </Form>
            </DialogContent>
            <DialogActions className={'p-0'}>
                <Box sx={{margin: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%'}}>
                    <Button
                        disabled={submitting}
                        variant={'dialog'}
                        color={'primary'}
                        onClick={close}
                        borderRadius={'0 8px 0 8px'}
                    >
                        {t('cancel')}
                    </Button>
                    <Button
                        disabled={submitting}
                        variant={'dialog'}
                        color={'primary'}
                        type={'submit'}
                        form={'create-vendor-form'}
                        borderRadius={'8px 0 8px 0'}
                    >
                        {submitting ? t('creating') : t('create')}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default CreateVendorDialog;
